import { PatientId, WithPatientId } from '../surgicalEvent.slice';
import { doctorBffApi } from '@/modules';
import { LabTestService } from './labTest.service';
import { GroupedLabTests, LabTest, LabTestType } from './labTest.model';
import { wrapError } from '../../error';
import { constructProtocolCacheKey, ProtocolName } from '@/modules/protocol';

export const labTestApi = doctorBffApi.injectEndpoints({
  endpoints: (builder) => ({
    labTests: builder.query<GroupedLabTests, PatientId>({
      queryFn: ({ patientId }) =>
        LabTestService.getInstance(patientId)
          .fetchGrouped<LabTestType, true>()
          .catch(wrapError),
      providesTags: (result, error, { patientId }) => [
        { type: 'LabTest', id: patientId }
      ]
    }),

    createEmptyLabTest: builder.mutation<
      GroupedLabTests,
      WithPatientId<{
        type: LabTestType;
      }>
    >({
      queryFn: ({ patientId, type }) =>
        LabTestService.getInstance(patientId)
          .createEmpty(type)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId }) => [
        { type: 'LabTest', id: patientId },
        { type: 'ProtocolStatuses', id: patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId,
            protocol: ProtocolName.ANEMIA
          })
        },
        'Dashboard'
      ]
    }),

    patchLabTest: builder.mutation<LabTest, WithPatientId<Partial<LabTest>>>({
      queryFn: ({ patientId, ...labTest }) =>
        LabTestService.getInstance(patientId).patch(labTest).catch(wrapError),
      invalidatesTags: (result, error, { patientId, ...labTest }) => {
        if (error) {
          return [];
        }

        const arr: any[] = [
          { type: 'LabTest', id: patientId },
          { type: 'ProtocolStatuses', id: patientId },
          {
            type: 'Protocol',
            id: constructProtocolCacheKey({
              patientId,
              protocol: ProtocolName.ANEMIA
            })
          },
          'ConsultationStat',
          'Dashboard'
        ];

        if ('hemoglobin' in labTest) {
          arr.push({
            type: 'Scores',
            id: patientId
          });
          arr.push({ type: 'MedicalHo', id: patientId });
        }

        if (
          result?.type === LabTestType.BLOODWORK ||
          result?.type === LabTestType.CARDIAC_ECHO
        ) {
          arr.push({ type: 'SurgeryInfo', id: patientId });
        }

        return arr;
      }
    }),

    deleteLabTest: builder.mutation<GroupedLabTests, WithPatientId<LabTest>>({
      queryFn: ({ patientId, ...labTest }) =>
        LabTestService.getInstance(patientId)
          .deleteGrouped(labTest)
          .catch(wrapError),
      invalidatesTags: (result, error, { patientId }) => [
        { type: 'LabTest', id: patientId },
        { type: 'ProtocolStatuses', id: patientId },
        {
          type: 'Protocol',
          id: constructProtocolCacheKey({
            patientId,
            protocol: ProtocolName.ANEMIA
          })
        },
        'ConsultationStat',
        'Dashboard'
      ],
      onQueryStarted: async (
        { patientId, ...deletedLabTest },
        { dispatch, queryFulfilled }
      ) => {
        const optimisticPatchResult = dispatch(
          labTestApi.util.updateQueryData(
            'labTests',
            { patientId },
            (groupedLabTests) => {
              const deletedId =
                typeof deletedLabTest === 'string'
                  ? deletedLabTest
                  : deletedLabTest.id;
              for (const key of Object.keys(groupedLabTests)) {
                const labTestType = key as LabTestType;
                const labTestList = groupedLabTests[labTestType];
                const found = !!labTestList?.find((lt) => lt.id === deletedId);
                if (found) {
                  groupedLabTests[labTestType] = labTestList!.filter(
                    (lt) => lt.id !== deletedId
                  );
                  break;
                }
              }

              return groupedLabTests;
            }
          )
        );

        try {
          await queryFulfilled;
        } catch {
          optimisticPatchResult.undo();
        }
      }
    })
  }),
  overrideExisting: false
});

export const {
  useLabTestsQuery,
  useCreateEmptyLabTestMutation,
  usePatchLabTestMutation,
  useDeleteLabTestMutation
} = labTestApi;
